import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { assessmentsApi, keysApi, type Assessment, type ApiKey } from '../lib/api';

const REVENUE_LABELS: Record<string, string> = {
  under50k: '< $50K',
  '50to200k': '$50K–$200K',
  over200k: '> $200K',
};

export default function Assessments() {
  const navigate = useNavigate();
  const [items, setItems] = useState<Assessment[]>([]);
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Filters
  const [filterKey, setFilterKey] = useState('');
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');

  const load = (p = page) => {
    setLoading(true);
    assessmentsApi
      .list({
        page: p,
        limit: 25,
        keyId: filterKey || undefined,
        from: filterFrom || undefined,
        to: filterTo || undefined,
      })
      .then((res) => {
        setItems(res.data);
        setTotalPages(res.pagination.pages);
        setTotal(res.pagination.total);
      })
      .catch(() => setError('Failed to load assessments'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    keysApi.list().then(setKeys).catch(() => {});
  }, []);

  useEffect(() => {
    load(1);
    setPage(1);
  }, [filterKey, filterFrom, filterTo]);

  useEffect(() => {
    load(page);
  }, [page]);

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} mb={3}>
        Assessment Log
        {!loading && (
          <Typography component="span" variant="body2" color="text.secondary" ml={1}>
            ({total.toLocaleString()} total)
          </Typography>
        )}
      </Typography>

      {/* Filters */}
      <Stack direction="row" spacing={2} mb={3} flexWrap="wrap">
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>API Key</InputLabel>
          <Select
            value={filterKey}
            label="API Key"
            onChange={(e) => setFilterKey(e.target.value)}
          >
            <MenuItem value="">All keys</MenuItem>
            {keys.map((k) => (
              <MenuItem key={k._id} value={k._id}>
                {k.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label="From"
          type="date"
          size="small"
          value={filterFrom}
          onChange={(e) => setFilterFrom(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="To"
          type="date"
          size="small"
          value={filterTo}
          onChange={(e) => setFilterTo(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <CircularProgress />
      ) : (
        <>
          <Paper variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Timestamp</TableCell>
                  <TableCell>API Key</TableCell>
                  <TableCell>Entity</TableCell>
                  <TableCell>Revenue</TableCell>
                  <TableCell>States</TableCell>
                  <TableCell align="right">Total Filings</TableCell>
                  <TableCell align="right">Response ms</TableCell>
                  <TableCell>Format</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      No assessments found
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((a) => (
                    <TableRow
                      key={a._id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/assessments/${a._id}`)}
                    >
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {new Date(a.requestedAt).toLocaleString()}
                      </TableCell>
                      <TableCell>{a.metadata.apiKeyLabel}</TableCell>
                      <TableCell>
                        <code style={{ fontSize: 12 }}>{a.params.entity}</code>
                      </TableCell>
                      <TableCell>
                        {REVENUE_LABELS[a.params.revenue] ?? a.params.revenue}
                      </TableCell>
                      <TableCell>
                        <Tooltip
                          title={
                            a.params.states?.length
                              ? a.params.states.join(', ')
                              : 'None'
                          }
                        >
                          <Chip
                            label={`${a.params.states?.length ?? 0} state${a.params.states?.length === 1 ? '' : 's'}`}
                            size="small"
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">
                        <strong>{a.resultSummary?.total ?? '—'}</strong>
                      </TableCell>
                      <TableCell align="right">{a.responseTimeMs ?? '—'}</TableCell>
                      <TableCell>
                        <Chip
                          label={a.params.format ?? 'full'}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Paper>

          {totalPages > 1 && (
            <Box display="flex" justifyContent="center" mt={2}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, p) => setPage(p)}
                color="primary"
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
